import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  FlaskConical, Search, Wallet, PlusCircle, MinusCircle,
  RotateCcw, History, Trophy, User as UserIcon, Loader2, ShieldAlert,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const QUICK_AMOUNTS = [50, 100, 500, 1000, 5000];
const BRL = (v) =>
  Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export default function AdminQAWalletTab() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null); // {user_id, name, email, wallet_balance}
  const [customAmount, setCustomAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const doSearch = useCallback(async () => {
    if (!query.trim()) {
      setResults([]);
      return;
    }
    setSearching(true);
    try {
      const r = await axios.get(`${API}/admin/users`, {
        params: { q: query.trim(), limit: 15 },
      });
      setResults(r.data.users || []);
    } catch (err) {
      console.error("[QA] search", err);
      toast.error("Falha ao buscar usuários");
    } finally {
      setSearching(false);
    }
  }, [query]);

  // Debounced live search
  useEffect(() => {
    const t = setTimeout(() => {
      if (query.trim().length >= 2) doSearch();
      else setResults([]);
    }, 350);
    return () => clearTimeout(t);
  }, [query, doSearch]);

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const r = await axios.get(`${API}/admin/wallet/demo/history`, {
        params: { limit: 30 },
      });
      setHistory(r.data.items || []);
    } catch (err) {
      console.error("[QA] history", err);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const refreshSelected = async (uid) => {
    try {
      const r = await axios.get(`${API}/admin/users/${uid}/full`);
      setSelected({
        user_id: uid,
        name: r.data.profile?.name,
        email: r.data.profile?.email,
        wallet_balance: r.data.wallet?.balance || 0,
      });
    } catch (err) {
      console.error("[QA] refresh selected", err);
    }
  };

  const pickUser = (u) => {
    setSelected({
      user_id: u.user_id,
      name: u.name,
      email: u.email,
      wallet_balance: u.wallet_balance || 0,
    });
    setResults([]);
    setQuery("");
  };

  const doAction = async (kind, amount) => {
    if (!selected) return toast.error("Selecione um usuário primeiro");
    const amt = Number(amount);
    if (kind !== "reset" && (!amt || amt <= 0)) {
      return toast.error("Valor inválido");
    }
    setBusy(true);
    try {
      let url = "";
      let payload = {};
      if (kind === "credit") {
        url = `${API}/admin/wallet/demo/credit`;
        payload = { target_user_id: selected.user_id, amount: amt, note: note || undefined };
      } else if (kind === "debit") {
        url = `${API}/admin/wallet/demo/debit`;
        payload = { target_user_id: selected.user_id, amount: amt, note: note || undefined };
      } else if (kind === "reset") {
        url = `${API}/admin/wallet/demo/reset`;
        payload = { target_user_id: selected.user_id };
      }
      const r = await axios.post(url, payload);
      const newBal = r.data.new_balance ?? 0;
      setSelected((s) => ({ ...s, wallet_balance: newBal }));
      if (kind === "credit") toast.success(`+${BRL(amt)} → ${selected.name || selected.email}`);
      if (kind === "debit") toast.success(`-${BRL(amt)} → ${selected.name || selected.email}`);
      if (kind === "reset") toast.success(`Saldo zerado (${BRL(r.data.previous_balance || 0)} removidos)`);
      setNote("");
      loadHistory();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Falha na operação";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const simulateRanking = async () => {
    if (!selected) return toast.error("Selecione um usuário para creditar o preset");
    setBusy(true);
    try {
      await axios.post(`${API}/admin/wallet/demo/credit`, {
        target_user_id: selected.user_id,
        amount: 500,
        note: "preset: simular ranking (R$500)",
      });
      toast.success("Preset aplicado: +R$500 para simular ranking");
      await refreshSelected(selected.user_id);
      loadHistory();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha no preset");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6 max-w-6xl" data-testid="admin-qa-wallet-root">
      {/* Header banner */}
      <Card className="p-5 border-red-500/40 bg-red-500/5">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-red-600 flex items-center justify-center flex-shrink-0">
            <FlaskConical className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h2 className="text-lg md:text-xl font-bold">QA Wallet — Dinheiro Fake</h2>
              <Badge variant="destructive" className="text-[10px]">MODO QA</Badge>
            </div>
            <p className="text-sm text-muted-foreground">
              Painel interno para testar fluxos de carteira, ranking, anúncios e escrow sem
              usar Pagar.me. Todas as transações ficam marcadas como <code className="text-xs bg-black/10 px-1 rounded">source: "demo"</code>.
            </p>
          </div>
        </div>
      </Card>

      {/* User search */}
      <Card className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <Search className="w-4 h-4 text-muted-foreground" />
          <Label className="text-sm font-semibold">Buscar usuário (email, nome, CPF ou ID)</Label>
        </div>
        <Input
          data-testid="qa-wallet-search-input"
          placeholder="Digite pelo menos 2 caracteres..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="mb-2"
        />
        {searching && (
          <div className="text-xs text-muted-foreground flex items-center gap-2">
            <Loader2 className="w-3 h-3 animate-spin" /> buscando...
          </div>
        )}
        {results.length > 0 && (
          <div className="mt-2 border rounded-lg divide-y max-h-64 overflow-auto" data-testid="qa-wallet-search-results">
            {results.map((u) => (
              <button
                key={u.user_id}
                type="button"
                onClick={() => pickUser(u)}
                data-testid={`qa-wallet-search-result-${u.user_id}`}
                className="w-full text-left px-3 py-2 hover:bg-accent/50 transition flex items-center gap-3"
              >
                <div className="w-8 h-8 rounded-full bg-primary/15 flex items-center justify-center">
                  <UserIcon className="w-4 h-4 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{u.name || "(sem nome)"}</div>
                  <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                </div>
                <div className="text-xs font-semibold text-emerald-600">
                  {BRL(u.wallet_balance)}
                </div>
              </button>
            ))}
          </div>
        )}
      </Card>

      {/* Selected user + actions */}
      {selected ? (
        <Card className="p-5" data-testid="qa-wallet-selected-card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                <UserIcon className="w-5 h-5 text-primary" />
              </div>
              <div>
                <div className="font-bold" data-testid="qa-wallet-selected-name">
                  {selected.name || "(sem nome)"}
                </div>
                <div className="text-xs text-muted-foreground">{selected.email}</div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Saldo</div>
              <div className="text-2xl font-black tabular-nums text-emerald-600" data-testid="qa-wallet-balance">
                {BRL(selected.wallet_balance)}
              </div>
            </div>
          </div>

          {/* Quick credit shortcuts */}
          <div className="mb-4">
            <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-2 block">
              Créditos rápidos
            </Label>
            <div className="flex flex-wrap gap-2">
              {QUICK_AMOUNTS.map((amt) => (
                <Button
                  key={amt}
                  variant="outline"
                  size="sm"
                  disabled={busy}
                  onClick={() => doAction("credit", amt)}
                  data-testid={`qa-wallet-quick-credit-${amt}`}
                  className="border-emerald-500/50 text-emerald-700 hover:bg-emerald-500/10 hover:text-emerald-800"
                >
                  <PlusCircle className="w-3 h-3 mr-1" />
                  R$ {amt.toLocaleString("pt-BR")}
                </Button>
              ))}
            </div>
          </div>

          {/* Custom amount + note */}
          <div className="grid md:grid-cols-3 gap-3 mb-4">
            <div>
              <Label className="text-xs">Valor customizado</Label>
              <Input
                type="number"
                min="0.01"
                step="0.01"
                placeholder="0,00"
                value={customAmount}
                onChange={(e) => setCustomAmount(e.target.value)}
                data-testid="qa-wallet-custom-amount"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Nota (opcional, aparece no histórico)</Label>
              <Input
                placeholder="ex: teste de escrow, ranking week 12..."
                value={note}
                onChange={(e) => setNote(e.target.value)}
                data-testid="qa-wallet-note"
              />
            </div>
          </div>

          {/* Custom actions */}
          <div className="flex flex-wrap gap-2 mb-4">
            <Button
              disabled={busy || !customAmount}
              onClick={() => doAction("credit", customAmount)}
              data-testid="qa-wallet-btn-credit-custom"
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <PlusCircle className="w-4 h-4 mr-2" />
              Creditar
            </Button>
            <Button
              disabled={busy || !customAmount}
              onClick={() => doAction("debit", customAmount)}
              data-testid="qa-wallet-btn-debit-custom"
              variant="outline"
              className="border-orange-500/50 text-orange-700 hover:bg-orange-500/10"
            >
              <MinusCircle className="w-4 h-4 mr-2" />
              Debitar
            </Button>
            <Button
              disabled={busy}
              onClick={() => {
                if (window.confirm("Zerar o saldo desse usuário?")) doAction("reset");
              }}
              data-testid="qa-wallet-btn-reset"
              variant="outline"
              className="border-red-500/50 text-red-700 hover:bg-red-500/10"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Zerar saldo
            </Button>
          </div>

          {/* Presets */}
          <div className="pt-4 border-t">
            <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-2 block">
              Presets QA
            </Label>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={simulateRanking}
                data-testid="qa-wallet-preset-ranking"
                className="border-amber-500/50 text-amber-700 hover:bg-amber-500/10"
              >
                <Trophy className="w-3 h-3 mr-1" />
                Simular Ranking (+R$500)
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={() => refreshSelected(selected.user_id)}
                data-testid="qa-wallet-refresh"
              >
                Atualizar saldo
              </Button>
            </div>
          </div>
        </Card>
      ) : (
        <Card className="p-8 text-center text-muted-foreground border-dashed" data-testid="qa-wallet-empty-selection">
          <ShieldAlert className="w-8 h-8 mx-auto mb-2 opacity-50" />
          Selecione um usuário acima para começar a testar.
        </Card>
      )}

      {/* History */}
      <Card className="p-5" data-testid="qa-wallet-history-card">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <History className="w-4 h-4" />
            <h3 className="font-semibold">Últimas operações demo</h3>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={loadHistory}
            disabled={loadingHistory}
            data-testid="qa-wallet-history-refresh"
          >
            {loadingHistory ? <Loader2 className="w-3 h-3 animate-spin" /> : "Atualizar"}
          </Button>
        </div>
        {history.length === 0 ? (
          <div className="text-sm text-muted-foreground text-center py-6">
            Nenhuma operação demo ainda.
          </div>
        ) : (
          <div className="divide-y">
            {history.map((tx) => (
              <div
                key={tx.tx_id}
                className="py-3 flex items-center gap-3"
                data-testid={`qa-wallet-history-item-${tx.tx_id}`}
              >
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    tx.type === "deposit"
                      ? "bg-emerald-500/15 text-emerald-600"
                      : "bg-orange-500/15 text-orange-600"
                  }`}
                >
                  {tx.type === "deposit" ? (
                    <PlusCircle className="w-4 h-4" />
                  ) : (
                    <MinusCircle className="w-4 h-4" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">
                    {tx.target_name || tx.target_email || tx.user_id}
                  </div>
                  <div className="text-[11px] text-muted-foreground truncate">
                    por {tx.admin_name || "admin"} · {new Date(tx.created_at).toLocaleString("pt-BR")}
                    {tx.note && <> · <em>{tx.note}</em></>}
                  </div>
                </div>
                <div
                  className={`text-sm font-bold tabular-nums ${
                    tx.type === "deposit" ? "text-emerald-600" : "text-orange-600"
                  }`}
                >
                  {tx.type === "deposit" ? "+" : "-"}
                  {BRL(tx.amount)}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
